import { FC, useEffect, useRef, useState } from 'react'
import { Link, useLoaderData, Navigate, type LoaderFunctionArgs, type RouteObject } from 'react-router-dom'
import { useUser } from '@clerk/clerk-react'
import './referral.css'

// COMPONENTS
import * as Icons from '@icons'
import Button from '@components/atoms/button'
import { Column } from '@components/templates/column'
import Divider from '@components/atoms/divider'
import Spinner from '@components/atoms/spinner'

// UTILITIES
import { VirtualCardAPI } from '@apis'
import type { PromoResponse } from '@apis/virtual-card/get-promo'
import { useLoader } from '@utils/loaders'


export function usePromoLoaderData() {
    return useLoaderData() as PromoResponse | { status: 'failed', error: any }
}

const Component: FC<{}> = () => {
    const { referralId } = useLoader<typeof loader>()
    const { isSignedIn, user } = useUser()
    const [data, setData] = useState<PromoResponse | { status: 'failed', error: any }>()
    const [isLoading, setIsLoading] = useState(true)

    useEffect(() => {
        setIsLoading(true)
        VirtualCardAPI.GetPromoRequest.fetch(referralId, isSignedIn)
            .then(setData)
            .catch((error) => setData({ status: 'failed', error }))
            .finally(() => { setTimeout(() => setIsLoading(false), 1000) })
    }, [referralId, isSignedIn])

    const ref = useRef<HTMLDialogElement>(null)
    useEffect(() => {
        if (ref.current) {
            if (isLoading) ref.current.showModal()
            else ref.current.close()
        }
    }, [isLoading])

    const promo = data?.status === 'successful' ? data.promo : null
    // Use canClaim for eligibility - if not returned, default to eligible
    const isEligible = data?.status === 'successful' && (promo?.canClaim !== false)

    if (!isEligible && !!user?.id && (user?.id === promo?.user?.id)) return <Navigate to={`/account?referral=ineligible`} replace={true} />
    else if (isLoading) return (
        <dialog ref={ref}>
            <div>
                <div className="flex flex-col items-center justify-center" style={{ background: 'var(--splitcard-white)', gap: '1rem', padding: '1rem', borderRadius: 'var(--border-radius)', overflowY: 'auto' }}>
                    <Spinner />
                </div>
            </div>
        </dialog>
    )
    else return (
        <Column className="referral-stack">
            <Column.Content className="referral-screen">
                <Column.Header.End>
                    <Link className="account-redirect-container" to="/account">
                        <Icons.House tint="var(--splitcard-black)" />
                    </Link>
                </Column.Header.End>

                {/* Content */}
                <div className="referral-content">
                    {isEligible ? (
                        <EligibleState
                            givenName={promo?.user?.givenName || 'Someone'}
                            amount={promo?.amount || 0}
                        />
                    ) : (
                        <IneligibleState />
                    )}
                </div>

                {/* Footer */}
                <Column.Footer>
                    <Button to="/account" style={{ width: '100%' }}>Account Page</Button>
                </Column.Footer>
            </Column.Content>
        </Column>
    )
}

// Eligible State Component
interface EligibleStateProps {
    givenName: string
    amount: number
}

const EligibleState: FC<EligibleStateProps> = ({ givenName, amount }) => {
    return (
        <>
            <div className="referral-header-card">
                <div className="referral-icon eligible">
                    <Icons.GiftBox width="1.5rem" height="1.5rem" tint="white" />
                </div>

                <h1 className="referral-title">
                    {givenName} invited you to <span className='text-blue-500'>Splitcard</span>
                </h1>

                <p className="referral-subtitle">
                    Organize your first group and get ${amount / 100} credited for future use.
                </p>
            </div>

            <Divider align="left" className="referral-section-title">How it works</Divider>

            <ol className="referral-steps">
                <li className="referral-step">
                    <div className="referral-step-icon blue">
                        <Icons.AddUser width="1.25rem" height="1.25rem" tint="#3B82F6" />
                    </div>
                    <div className="referral-step-content">
                        <h4 className="referral-step-title">Sign Up</h4>
                        <p className="referral-step-description">Create and verify your account.</p>
                    </div>
                </li>

                <li className="referral-step">
                    <div className="referral-step-icon purple">
                        <Icons.Group width="1.25rem" height="1.25rem" tint="#A855F7" />
                    </div>
                    <div className="referral-step-content">
                        <h4 className="referral-step-title">Organize a Group</h4>
                        <p className="referral-step-description">
                            Send a payment link to your group to start pooling money.
                            Spend at least $10 and include 2 or more participants.
                        </p>
                    </div>
                </li>

                <li className="referral-step">
                    <div className="referral-step-icon green">
                        <Icons.GiftBox width="1.25rem" height="1.25rem" tint="#10B981" />
                    </div>
                    <div className="referral-step-content">
                        <h4 className="referral-step-title">Earn Credit</h4>
                        <p className="referral-step-description">
                            Receive your <span className="credit">${amount / 100} credit</span> for future use after your first group finalizes.
                        </p>
                    </div>
                </li>
            </ol>
        </>
    )
}

// Ineligible State Component
const IneligibleState: FC<{}> = () => {
    return (
        <>
            <div className="referral-header-card">
                <div className="referral-icon ineligible">
                    <Icons.WarningSmall width="1.5rem" height="1.5rem" tint="#F59E0B" />
                </div>

                <h1 className="referral-title">
                    You are not eligible for<br />this referral program
                </h1>

                <div className="referral-status-badge">
                    <Icons.GiftBox width="1.5rem" height="1.5rem" tint="var(--splitcard-gray-400)" />
                    <div className="referral-status-info">
                        <span className="referral-status-label">Reward Status</span>
                        <span className="referral-status-value">Unavailable</span>
                    </div>
                    <span className="referral-status-pill">Ineligible</span>
                </div>
            </div>

            <Divider align="left" className="referral-section-title">Common Reasons</Divider>

            <ol className="referral-steps">
                <li className="referral-step">
                    <div className="referral-step-icon blue">
                        <Icons.AddUser width="1.25rem" height="1.25rem" tint="#3B82F6" />
                    </div>
                    <div className="referral-step-content">
                        <h4 className="referral-step-title">Existing Account</h4>
                        <p className="referral-step-description">
                            This referral offer is valid for new users only. It looks like you're already with us!
                        </p>
                    </div>
                </li>

                <li className="referral-step">
                    <div className="referral-step-icon purple">
                        <Icons.Clock width="1.25rem" height="1.25rem" tint="#A855F7" />
                    </div>
                    <div className="referral-step-content">
                        <h4 className="referral-step-title">Promotion Expired</h4>
                        <p className="referral-step-description">
                            This promotion link is not valid anymore.
                        </p>
                    </div>
                </li>
            </ol>

            <div style={{ borderRadius: 'var(--border-radius-lrg)', margin: 'auto var(--horizontal-content-padding) 2rem' }}>
                <div className="referral-contact-card">
                    <p className="referral-contact-title">Contact Support</p>
                    <div className="referral-contact-row">
                        <Icons.Phone tint="var(--splitcard-blue-500)" height="1rem" width="1rem" />
                        <a onClick={() => window.open("tel:+18886254349")}>888-625-4349</a>
                    </div>
                    <div className="referral-contact-row">
                        <Icons.Email tint="var(--splitcard-blue-500)" height="1rem" width="1rem" />
                        <a onClick={() => window.open("mailto:support@splitcard.com")}>support@splitcard.com</a>
                    </div>
                </div>
            </div>
        </>
    )
}

export async function loader({ params }: LoaderFunctionArgs) {
    const { referralId } = params

    if (!referralId) {
        throw new Response('No referral ID provided', { status: 400 })
    }

    return { referralId }
}

const ReferralRoute: RouteObject = {
    path: 'referral/:referralId',
    element: <Component />,
    loader
}

export default ReferralRoute
